'use client';
import './features.scss';
import { Container } from 'react-bootstrap';
import { LANDING_OR_PRODUCT_PAGE_QUERY_RESULT } from '@/sanity.types';
import { LandingPortableText } from '@/components/landingPortableText/landingPortableText';
import { PortableTextBlock } from '@portabletext/react';
import { useEffect, useState } from 'react';
import FeaturesMobile from './featuresMobile';
import QuoteBlock from './QuoteBlock';
import FeatureContentBlock from './FeatureContentBlock';
import Typography from '@/componentv2/typography/typography';

interface FeatureSectionProps {
  data: NonNullable<LANDING_OR_PRODUCT_PAGE_QUERY_RESULT>;
}

export default function Features({ data }: FeatureSectionProps) {
  const featureContent =
    data?._type === 'productPage' ? data?.featuresTiles : null;

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);
  return (
    <section className={`features-section`}>
      <Container>
        {featureContent?.whiteOnBlackLabel && (
          <Typography as="h2" style="eyebrow-badge">
            {featureContent?.whiteOnBlackLabel}
          </Typography>
        )}
        {featureContent?.title && (
          <div className="features-section-title">
            <LandingPortableText
              value={featureContent?.title as PortableTextBlock[]}
            />
          </div>
        )}
        <div className="features-content-wrapper">
          {isMobile ? (
            <FeaturesMobile data={data} />
          ) : (
            <>
              {featureContent?.featuresBricks?.map((brick, brickIndex) => (
                <div key={brickIndex}>
                  {brick?._type === 'megaBrick' && (
                    <div className="features-list-with-image">
                      <div className="features-list-left">
                        {brick?.bigBlock?.blockType === 'content' && (
                          <FeatureContentBlock
                            contentBlock={brick?.bigBlock?.contentBlock}
                          />
                        )}
                        {brick?.bigBlock?.blockType === 'quote' && (
                          <QuoteBlock
                            quote={brick?.bigBlock?.quoteBlock?.quote}
                            author={brick?.bigBlock?.quoteBlock?.author}
                            backgroundColor={
                              featureContent?.featuredColor?.value
                            }
                          />
                        )}
                      </div>
                      <div className="features-list-right">
                        {brick?.smallBlockTop?.blockType === 'content' && (
                          <FeatureContentBlock
                            contentBlock={brick?.smallBlockTop?.contentBlock}
                          />
                        )}
                        {brick?.smallBlockTop?.blockType === 'quote' && (
                          <QuoteBlock
                            quote={brick?.smallBlockTop?.quoteBlock?.quote}
                            author={brick?.smallBlockTop?.quoteBlock?.author}
                            backgroundColor={
                              featureContent?.featuredColor?.value
                            }
                          />
                        )}
                        {brick?.smallBlockBottom?.blockType === 'content' && (
                          <FeatureContentBlock
                            contentBlock={brick?.smallBlockBottom?.contentBlock}
                          />
                        )}
                        {brick?.smallBlockBottom?.blockType === 'quote' && (
                          <QuoteBlock
                            quote={brick?.smallBlockBottom?.quoteBlock?.quote}
                            author={brick?.smallBlockBottom?.quoteBlock?.author}
                            backgroundColor={
                              featureContent?.featuredColor?.value
                            }
                          />
                        )}
                      </div>
                    </div>
                  )}
                  {brick?._type === 'tripleColumn' && (
                    <div className="features-list">
                      {brick?.leftColumn?.blockType === 'content' && (
                        <FeatureContentBlock
                          contentBlock={brick?.leftColumn?.contentBlock}
                        />
                      )}
                      {brick?.leftColumn?.blockType === 'quote' && (
                        <QuoteBlock
                          quote={brick?.leftColumn?.quoteBlock?.quote}
                          author={brick?.leftColumn?.quoteBlock?.author}
                          backgroundColor={featureContent?.featuredColor?.value}
                        />
                      )}
                      {brick?.centerColumn?.blockType === 'content' && (
                        <FeatureContentBlock
                          contentBlock={brick?.centerColumn?.contentBlock}
                        />
                      )}
                      {brick?.centerColumn?.blockType === 'quote' && (
                        <QuoteBlock
                          quote={brick?.centerColumn?.quoteBlock?.quote}
                          author={brick?.centerColumn?.quoteBlock?.author}
                          backgroundColor={featureContent?.featuredColor?.value}
                        />
                      )}
                      {brick?.rightColumn?.blockType === 'content' && (
                        <FeatureContentBlock
                          contentBlock={brick?.rightColumn?.contentBlock}
                        />
                      )}
                      {brick?.rightColumn?.blockType === 'quote' && (
                        <QuoteBlock
                          quote={brick?.rightColumn?.quoteBlock?.quote}
                          author={brick?.rightColumn?.quoteBlock?.author}
                          backgroundColor={featureContent?.featuredColor?.value}
                        />
                      )}
                    </div>
                  )}
                  {brick?._type === 'doubleColumn' && (
                    <div className="features-list double-column-list">
                      {brick?.leftColumn?.blockType === 'content' && (
                        <FeatureContentBlock
                          contentBlock={brick?.leftColumn?.contentBlock}
                        />
                      )}
                      {brick?.leftColumn?.blockType === 'quote' && (
                        <QuoteBlock
                          quote={brick?.leftColumn?.quoteBlock?.quote}
                          author={brick?.leftColumn?.quoteBlock?.author}
                          backgroundColor={featureContent?.featuredColor?.value}
                        />
                      )}
                      {brick?.rightColumn?.blockType === 'content' && (
                        <FeatureContentBlock
                          contentBlock={brick?.rightColumn?.contentBlock}
                        />
                      )}
                      {brick?.rightColumn?.blockType === 'quote' && (
                        <QuoteBlock
                          quote={brick?.rightColumn?.quoteBlock?.quote}
                          author={brick?.rightColumn?.quoteBlock?.author}
                          backgroundColor={featureContent?.featuredColor?.value}
                        />
                      )}
                    </div>
                  )}
                  {brick?._type === 'singleColumn' && (
                    <>
                      {brick?.blockType === 'content' && (
                        <FeatureContentBlock
                          contentBlock={brick?.contentBlock}
                          className={`single-column-feature ${brick?.contentBlock?.image ? 'has-image-list' : ''} ${brick?.contentBlock?.toggleColumnBackgroundColor ? 'background' : 'no-background'}`}
                        />
                      )}
                      {brick?.blockType === 'quote' && (
                        <QuoteBlock
                          quote={brick?.quoteBlock?.quote || ''}
                          author={brick?.quoteBlock?.author || ''}
                          backgroundColor={featureContent?.featuredColor?.value}
                        />
                      )}
                    </>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </Container>
    </section>
  );
}
